'use client';

import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { useCallback, useEffect, useMemo, useState } from 'react';

import AppLoader from '@/components/ui/app-loader';

import { ScreenLayout } from '@/features/user-onboarding/components';
import { WorkspaceCreationForm } from '@/features/user-onboarding/components/screens/workspace-create-screen';
import { WorkspaceJoinScreen } from '@/features/user-onboarding/components/screens/workspace-join-screen';
import {
	ONBOARDING_SCREEN_HEADERS,
	OnboardingScreen,
} from '@/features/user-onboarding/constants/user-onboarding-constants';

import { LogoutLink } from '@/shared/components/logout-link';
import { extractDomainFromEmail, isBlockedDomain } from '@/shared/constants/blocked-domains';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { QUERY_KEY_INITIALS } from '@/shared/constants/query-key-initials';
import { useAppStore } from '@/shared/contexts/app-wrapper/providers/app-provider';
import { discoverWorkspaces } from '@/shared/services/workspace/discover';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';

interface Props {
	onSuccess?: (workspaceId: string) => void;
	source?: string;
	skipReload?: boolean;
}

export default function CreateNewWorkspace({ onSuccess, skipReload = false }: Props) {
	const onWorkspaceCreate = (workspaceId: string) => {
		if (!skipReload) {
			window.location.reload();
		}
		onSuccess?.(workspaceId);
	};

	return (
		<WorkspaceCreationForm
			workspaceName=""
			onWorkspaceNameChange={() => {}}
			className="text-start"
			showImageUpload
			onSuccess={onWorkspaceCreate}
		/>
	);
}

export function CreateNewWorkspaceFullScreen({ onSuccess, source, skipReload = false }: Props) {
	const userEmail = useAppStore((state) => state.userDetails?.email);
	const isDomainBlocked = useMemo(
		() => isBlockedDomain(extractDomainFromEmail(userEmail ?? '')),
		[userEmail]
	);
	const [showCreateForm, setShowCreateForm] = useState(false);

	const { data: discoverData, isLoading: isDiscoverLoading } = useQuery({
		queryKey: [QUERY_KEY_INITIALS.DISCOVER_WORKSPACES],
		queryFn: discoverWorkspaces,
		enabled: !isDomainBlocked,
	});

	const discoverableWorkspaces = discoverData?.pages ?? [];
	const hasDiscoverableWorkspaces = discoverableWorkspaces.length > 0;

	const onWorkspaceSuccess = useCallback(
		(workspaceId: string) => {
			if (!skipReload) {
				window.location.reload();
			}
			onSuccess?.(workspaceId);
		},
		[skipReload, onSuccess]
	);

	useEffect(() => {
		if (isDiscoverLoading) return;
		if (hasDiscoverableWorkspaces && !showCreateForm) return;

		const isNewOnboardingFeature = source === MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL;
		const event = isNewOnboardingFeature
			? mixpanelEvents.VIEWED_NEW_ONBOARDING_CREATE_WORKSPACE_SCREEN
			: mixpanelEvents.VIEWED_CREATE_WORKSPACE_SCREEN;

		emitMixpanelEvent(event, {
			feature: isNewOnboardingFeature
				? MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL
				: MIXPANEL_APP_FEATURES.USER_ONBOARDING,
			type: source,
		});
	}, [isDiscoverLoading, hasDiscoverableWorkspaces, showCreateForm, source]);

	if (isDiscoverLoading) {
		return (
			<div className="flex h-screen w-full items-center justify-center bg-slate-100">
				<AppLoader />
			</div>
		);
	}

	if (hasDiscoverableWorkspaces && !showCreateForm) {
		return (
			<WorkspaceJoinScreen
				workspaces={discoverableWorkspaces}
				onCreateNewClick={() => setShowCreateForm(true)}
				onJoinSuccess={(workspaceId) => onWorkspaceSuccess(workspaceId)}
				skipLayout={false}
				source={source}
			/>
		);
	}

	return (
		<ScreenLayout
			title={ONBOARDING_SCREEN_HEADERS[OnboardingScreen.WORKSPACE_DETAILS].title}
			description={ONBOARDING_SCREEN_HEADERS[OnboardingScreen.WORKSPACE_DETAILS]?.description}
			cardContent={
				<WorkspaceCreationForm
					workspaceName=""
					onWorkspaceNameChange={() => {}}
					className="w-full text-start"
					showImageUpload
					onSuccess={onWorkspaceSuccess}
				/>
			}
			topLeft={
				hasDiscoverableWorkspaces ? (
					<button
						type="button"
						className="flex items-center gap-1 text-sm text-secondary hover:text-primary"
						onClick={() => setShowCreateForm(false)}
					>
						<ArrowLeft className="size-3.5" />
						Back
					</button>
				) : null
			}
			topRight={<LogoutLink />}
		/>
	);
}
